import { GetStaticPropsResult } from 'next';

import { Box, Flex } from '@chakra-ui/react';

import { PageCategory } from '~/features/analytics/constants';
import {
  CatalogFilterDefinitionData,
  getCatalogFilterDataFromServer,
} from '~/features/catalog/controllers/get-catalog-filter-data-from-server';
import { getCatalogSearchResults } from '~/features/catalog/controllers/get-catalog-search-results';
import { CatalogSearchResults } from '~/features/catalog/models/catalog-search-result';
import { DEFAULT_SORT, UriFilter } from '~/features/catalog/models/filter';
import { Filters } from '~/features/catalog/models/filter';
import { Catalog } from '~/features/experiments/ab_plp_redesign_2025/components/catalog';
import {
  CarouselItem,
  CatalogHeroSection,
} from '~/features/experiments/ab_plp_redesign_2025/components/catalog-hero-section';
import {
  CatalogSubcategoriesSection,
  StaticCatalogSubcategoriesSectionProps,
  transformStaticCatalogSubcategoriesSectionProps,
} from '~/features/experiments/ab_plp_redesign_2025/components/catalog-subcategories-section';
import { useExperimentalSegmentEvents } from '~/features/experiments/ab_plp_redesign_2025/hooks';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { useI18n } from '~/hooks/use-i18n';
import { useScrollDepthEvent } from '~/hooks/use-scroll-depth-event';

type CatalogPageProps = {
  page: Page;
  catalogSearchResults: CatalogSearchResults | null;
  skillUriFilters: UriFilter[];
  schoolUriFilters: UriFilter[];
  staticCatalogSubcategoriesSectionProps: StaticCatalogSubcategoriesSectionProps;
};

export default function CatalogFreeTrialFlagOnPage(props: CatalogPageProps) {
  const { t, isJa } = useI18n();
  const experimentalSegmentEvents = useExperimentalSegmentEvents('dino');

  useScrollDepthEvent({
    threshold: [25, 50, 75, 99],
    analyticsProperties: {
      category: PageCategory.Catalog,
    },
    isEnabled: experimentalSegmentEvents?.variant === 'on',
  });

  const { page, catalogSearchResults, schoolUriFilters, skillUriFilters, staticCatalogSubcategoriesSectionProps } =
    props as CatalogPageProps;

  const carouselItems: CarouselItem[] = [
    {
      title: 'Try our **top Agentic AI\nNanodegree program** for free',
      ctaLabel: 'Try For Free',
      ctaUrl: '/course/agentic-ai--nd900',
      theme: 'free-trial',
      backgroundImage: '/images/experiments/ab_plp_redesign_2025/free-trial-card-bg.png',
      key: 'free-trial',
      overlay: true,
      eventPayload: {
        type: 'click' as const,
        name: 'catalogCtaClicked',
        label: 'agentic-ai-ft-2026 cta clicked',
        value: 'nd900',
      },
    },
    {
      title: t('catalog.hero.carousel.carouselItems.mbaAi.title'),
      ctaLabel: t('catalog.hero.carousel.carouselItems.mbaAi.ctaLabel'),
      ctaUrl: '/mba-ai-product-management',
      theme: 'mba-crimson',
      backgroundImage: '/images/experiments/ab_plp_redesign_2025/mba-card-bg.png',
      key: 'mba-ai-product-management',
      eventPayload: {
        type: 'click' as const,
        name: 'catalogCtaClicked',
        label: 'mba_entry_clicked',
        additionalAttributes: {
          formal_degree_key: 'mba_ai',
        },
      },
    },
    {
      title: t('catalog.hero.carousel.carouselItems.mastersAi.title'),
      ctaLabel: t('catalog.hero.carousel.carouselItems.mastersAi.ctaLabel'),
      ctaUrl: '/masters-artificial-intelligence',
      theme: 'woolf-gold',
      backgroundImage: '/images/experiments/ab_plp_redesign_2025/woolf-card-bg.png',
      key: 'masters-artificial-intelligence',
      eventPayload: {
        type: 'click' as const,
        name: 'catalogCtaClicked',
        label: 'masters_entry_clicked',
        additionalAttributes: {
          formal_degree_key: 'ms_ai',
        },
      },
    },
    {
      title: t('catalog.hero.carousel.carouselItems.nd608.title'),
      ctaLabel: t('catalog.hero.carousel.carouselItems.nd608.ctaLabel'),
      ctaUrl: '/course/generative-ai--nd608',
      theme: 'purple',
      backgroundImage: '/images/experiments/ab_plp_redesign_2025/purple-card-bg.png',
      key: 'nd608',
      eventPayload: {
        type: 'click' as const,
        name: 'catalogCtaClicked',
        label: 'view_course_clicked',
        value: 'nd608',
      },
    },
    {
      title: t('catalog.hero.carousel.carouselItems.nd089.title'),
      ctaLabel: t('catalog.hero.carousel.carouselItems.nd089.ctaLabel'),
      ctaUrl: '/course/ai-programming-python-nanodegree--nd089',
      theme: 'cyan',
      backgroundImage: '/images/experiments/ab_plp_redesign_2025/cyan-card-bg.png',
      key: 'nd089',
      eventPayload: {
        type: 'click' as const,
        name: 'catalogCtaClicked',
        label: 'view_course_clicked',
        value: 'nd089',
      },
    },
  ];

  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.Catalog} />
      <PageLayout withoutVerticalPadding>
        <Box bgColor='grey.50' paddingBottom={{ base: '56px', md: '112px' }}>
          <CatalogHeroSection itemsOverride={carouselItems} />
          <Flex
            flexDirection='column'
            paddingTop={{ base: '24px', md: '40px' }}
            gap={{
              base: '56px',
              md: '112px',
            }}
          >
            <Catalog
              initialCatalogSearchResults={catalogSearchResults}
              schoolUriFilters={schoolUriFilters}
              skillUriFilters={skillUriFilters}
            />
            {!isJa && <CatalogSubcategoriesSection {...staticCatalogSubcategoriesSectionProps} />}
          </Flex>
        </Box>
      </PageLayout>
    </>
  );
}

export async function getStaticProps(): Promise<GetStaticPropsResult<CatalogPageProps>> {
  // Use en-us locale metadata as default (client-side hook handles locale-specific data)
  const defaultLocaleMetadata = getCatalogFilterDataFromServer('en-us') as CatalogFilterDefinitionData;
  const skillUriFilters = defaultLocaleMetadata?.skills || [];
  const schoolUriFilters = defaultLocaleMetadata?.schools || [];

  const [page, staticCatalogSubcategoriesSectionProps] = await Promise.all([
    getPage('/catalog'),
    transformStaticCatalogSubcategoriesSectionProps(),
  ]);

  const catalogSearchResults = await getCatalogSearchResults({
    selectedFilters: {} as Filters,
    selectedSort: DEFAULT_SORT,
    page: 0,
    schoolUriFilters,
    skillUriFilters,
  });

  return {
    props: {
      page,
      catalogSearchResults,
      skillUriFilters,
      schoolUriFilters,
      staticCatalogSubcategoriesSectionProps,
    },
  };
}
